import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiUser, FiLock, FiSettings as FiSettingsIcon, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/config';
import { passwordAPI } from '../services/api';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [profileData, setProfileData] = useState({
    title: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    description: '',
    institute_type: 'online',
    tagline: '',
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
    linkedin: '',
    whatsapp: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setFetchingData(true);
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.get(`${API_BASE_URL}/settings/general`, {
        headers: { 'X-User-ID': userId }
      });
      
      if (response.data.success) {
        const { institute, settings } = response.data.data;
        
        setProfileData({
          title: settings?.title || institute?.name || '',
          email: settings?.email || institute?.email || '',
          phone: settings?.phone || institute?.phone || '',
          address: settings?.address || institute?.address || '',
          website: settings?.website || institute?.website || '',
          description: settings?.description || institute?.description || '',
          institute_type: institute?.institute_type || 'online',
          tagline: settings?.tagline || '',
          facebook: settings?.facebook || '',
          twitter: settings?.twitter || '',
          instagram: settings?.instagram || '',
          youtube: settings?.youtube || '',
          linkedin: settings?.linkedin || '',
          whatsapp: settings?.whatsapp || ''
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error loading settings');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/settings/general`,
        profileData,
        { headers: { 'X-User-ID': userId } }
      );

      if (response.data.success) {
        toast.success('Settings updated successfully');
        
        // Update user context with new data
        if (updateUser) {
          updateUser({
            ...user,
            name: profileData.title,
            email: profileData.email,
            phone: profileData.phone,
            address: profileData.address,
            website: profileData.website,
            description: profileData.description
          });
        }
        
        // Refresh settings
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await passwordAPI.changeStaffPassword(passwordData);
      
      if (response.data.success) {
        toast.success('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          new_password_confirmation: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-sm md:text-base text-gray-600">Manage your institute settings and preferences</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-md p-3 md:p-4 space-y-1 md:space-y-2">
            <button
              className={`w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm md:text-base min-h-[44px] ${
                activeTab === 'profile' 
                  ? 'bg-primary-600 text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('profile')}
            >
              <FiUser size={18} className="md:w-5 md:h-5" /> Profile Settings
            </button>
            <button
              className={`w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm md:text-base min-h-[44px] ${
                activeTab === 'security' 
                  ? 'bg-primary-600 text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('security')}
            >
              <FiLock size={18} className="md:w-5 md:h-5" /> Security
            </button>
            <button
              className={`w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm md:text-base min-h-[44px] ${
                activeTab === 'general' 
                  ? 'bg-primary-600 text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('general')}
            >
              <FiSettingsIcon size={18} className="md:w-5 md:h-5" /> General
            </button>
          </div>
        </div>

        <div className="flex-1 bg-white rounded-lg shadow-md p-6">
          {fetchingData ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Information</h2>
                  <p className="text-gray-600 mb-6">
                    Update your institute's basic information
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                          Institute Name *
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          value={profileData.title}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          value={profileData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          value={profileData.phone}
                          onChange={handleChange}
                        />
                      </div>

                      <div>
                        <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-2">
                          WhatsApp Number
                        </label>
                        <input
                          type="tel"
                          id="whatsapp"
                          name="whatsapp"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          value={profileData.whatsapp}
                          onChange={handleChange}
                          placeholder="+91 9876543210"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          id="website"
                          name="website"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          value={profileData.website}
                          onChange={handleChange}
                          placeholder="https://example.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="tagline" className="block text-sm font-medium text-gray-700 mb-2">
                          Tagline
                        </label>
                        <input
                          type="text"
                          id="tagline"
                          name="tagline"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                          value={profileData.tagline}
                          onChange={handleChange}
                          placeholder="Your institute's tagline"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                        Address
                      </label>
                      <textarea
                        id="address"
                        name="address"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        value={profileData.address}
                        onChange={handleChange}
                        rows={3}
                      ></textarea>
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        value={profileData.description}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Brief description about your institute"
                      ></textarea>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <label htmlFor="institute_type" className="block text-sm font-medium text-gray-900 mb-2">
                        Institute Type *
                      </label>
                      <select
                        id="institute_type"
                        name="institute_type"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white"
                        value={profileData.institute_type}
                        onChange={handleChange}
                        required
                      >
                        <option value="online">Online Only - Video courses, online tests, digital content</option>
                        <option value="offline">Offline Only - Physical classes, attendance, timetable</option>
                        <option value="library">Library - Book management, issue/return, memberships</option>
                        <option value="both">Both - All features (online + offline)</option>
                      </select>
                      <p className="text-xs text-gray-600 mt-2">
                        This determines which features are available in your portal. Choose "Both" if you need all features.
                      </p>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media Links</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-2">
                            Facebook
                          </label>
                          <input
                            type="url"
                            id="facebook"
                            name="facebook"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            value={profileData.facebook}
                            onChange={handleChange}
                            placeholder="https://facebook.com/yourpage"
                          />
                        </div>

                        <div>
                          <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-2">
                            Twitter
                          </label>
                          <input
                            type="url"
                            id="twitter"
                            name="twitter"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            value={profileData.twitter}
                            onChange={handleChange}
                            placeholder="https://twitter.com/yourhandle"
                          />
                        </div>

                        <div>
                          <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                            Instagram
                          </label>
                          <input
                            type="url"
                            id="instagram"
                            name="instagram"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            value={profileData.instagram}
                            onChange={handleChange}
                            placeholder="https://instagram.com/yourpage"
                          />
                        </div>

                        <div>
                          <label htmlFor="youtube" className="block text-sm font-medium text-gray-700 mb-2">
                            YouTube
                          </label>
                          <input
                            type="url"
                            id="youtube"
                            name="youtube"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            value={profileData.youtube}
                            onChange={handleChange}
                            placeholder="https://youtube.com/yourchannel"
                          />
                        </div>

                        <div>
                          <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-2">
                            LinkedIn
                          </label>
                          <input
                            type="url"
                            id="linkedin"
                            name="linkedin"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            value={profileData.linkedin}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/company/yourcompany"
                          />
                        </div>
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
                      disabled={loading}
                    >
                      <FiSave /> {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'security' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Change Password</h2>
                  <p className="text-gray-600 mb-6">
                    Update your password to keep your account secure
                  </p>

                  <form onSubmit={handlePasswordChange} className="space-y-6 max-w-xl">
                    <div>
                      <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-2">
                        Current Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          id="current_password"
                          value={passwordData.current_password}
                          onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                          placeholder="Enter current password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-2">
                        New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          id="new_password"
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                          placeholder="Enter new password (min 6 characters)"
                          required
                          minLength="6"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showNewPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirm_password"
                          value={passwordData.new_password_confirmation}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password_confirmation: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                          placeholder="Confirm new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                    </div>

                    <button 
                      type="submit"
                      className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                    >
                      <FiLock /> {loading ? 'Changing...' : 'Change Password'}
                    </button>
                  </form>
                </div>
              )}

          {activeTab === 'general' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">General Settings</h2>
              <p className="text-gray-600 mb-6">
                Configure general preferences for your institute
              </p>

              <div className="bg-gradient-to-br from-primary-50 to-purple-50 rounded-lg p-6 border border-primary-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Plan</h3>
                <p className="text-2xl font-bold text-primary-600 mb-4">
                  {user?.subscription_plan?.toUpperCase() || 'FREE'}
                </p>
                <p className="text-sm text-gray-700 mb-6">
                  Max Teachers: <span className="font-semibold">{user?.max_teachers || 5}</span> | 
                  Max Courses: <span className="font-semibold">{user?.max_courses || 10}</span> | 
                  Max Students: <span className="font-semibold">{user?.max_students || 100}</span>
                </p>
                <button className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium">
                  Upgrade Plan
                </button>
              </div>
            </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;

